import React, { useEffect, useState } from "react";
import Sidebar from "./components/Sidebar";
import Welcome from "./pages/Welcome";
import AgentChat from "./pages/AgentChat";
import JobRequisition from "./pages/JobRequisition";
import Requisitions from "./pages/Requisitions";
import AIInterviews from "./pages/AIInterviews";
import AIInterviewSession from "./pages/AIInterviewSession";
import CVEvaluation from "./pages/CVEvaluation";
import CandidateList from "./pages/CandidateList";
import RecruitmentDashboard from "./pages/RecruitmentDashboard";
import CandidateScreening from "./pages/CandidateScreening";
import ScheduleInterviews from "./pages/ScheduleInterviews";
import Communication from "./pages/Communication";
import CandidateStatus from "./pages/CandidateStatus";
import Insights from "./pages/Insights";
import AdminSettings from "./pages/AdminSettings";
import CandidatePortal from "./pages/CandidatePortal";
import Login from "./pages/Login";
import { http } from "./lib/api";
import { AuthProvider, useAuth } from "./lib/auth";

function Shell() {
  const { user, ready, logout } = useAuth();
  const [page, setPage] = useState("welcome");
  const [screeningCandidate, setScreeningCandidate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [locationFilter, setLocationFilter] = useState("All");
  const [requisitionFilter, setRequisitionFilter] = useState("All");
  const [requisitionOptions, setRequisitionOptions] = useState([]);
  const [preselectedRequisitionId, setPreselectedRequisitionId] = useState(null);

  const refreshRequisitions = React.useCallback(() => {
    return http.get("/fusion/requisitions").then((r) => {
      const items = (r.data && r.data.items) || [];
      setRequisitionOptions(items.map((i) => ({
        value: i.RequisitionNumber,
        label: `${i.RequisitionNumber} · ${i.Title || "Untitled"}`,
      })));
    }).catch(() => setRequisitionOptions([]));
  }, []);

  useEffect(() => {
    if (user && user.id) {
      refreshRequisitions();
    }
  }, [user, refreshRequisitions]);

  // Chat deep-link router. Called when the HR chat emits a vaku:// pill.
  const handleDeepLink = React.useCallback((url) => {
    // vaku://candidate/<id> | vaku://requisition/<id>
    const m = /^vaku:\/\/([a-z]+)\/(.+)$/i.exec(url || "");
    if (!m) return;
    const [, kind, rawId] = m;
    const id = decodeURIComponent(rawId);
    if (kind === "candidate") {
      // Candidate ids in this app are numeric. Coerce when possible so the
      // Screening page's `id === preselected.id` comparison hits.
      const numeric = /^-?\d+$/.test(id) ? parseInt(id, 10) : id;
      setScreeningCandidate({ id: numeric });
      setPage("screening");
    } else if (kind === "requisition") {
      setPreselectedRequisitionId(id);
      setPage("requisitions-manage");
    }
  }, []);

  if (!ready) {
    return <div className="min-h-screen grid place-items-center text-slate-400 text-sm">Loading…</div>;
  }
  if (!user || user === false) {
    return <Login />;
  }

  const onNavigate = (key) => {
    setPage(key);
    if (key !== "screening") setScreeningCandidate(null);
    if (key !== "requisitions-manage") setPreselectedRequisitionId(null);
  };

  // The Job Requisition page always needs a concrete requisition number.
  // Derive one from the sidebar dropdown, falling back to the first live req.
  const activeRequisition = (requisitionFilter && requisitionFilter !== "All" && requisitionFilter !== "__unassigned__")
    ? requisitionFilter
    : (requisitionOptions[0]?.value || "");

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        current={page}
        onChange={onNavigate}
        user={user}
        onLogout={logout}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        locationFilter={locationFilter}
        setLocationFilter={setLocationFilter}
        requisitionFilter={requisitionFilter}
        setRequisitionFilter={setRequisitionFilter}
        requisitionOptions={requisitionOptions}
      />
      <main className="flex-1 overflow-y-auto p-6 md:p-8" data-testid="main-content">
        {page === "welcome" && <Welcome onNavigate={onNavigate} />}
        {page === "hr-chat" && <AgentChat agent="hr" onDeepLink={handleDeepLink} />}
        {page === "requisition" && <JobRequisition requisitionNumber={activeRequisition} />}
        {page === "requisitions-manage" && (
          <Requisitions
            onChanged={refreshRequisitions}
            preselectedRequisitionId={preselectedRequisitionId}
            onPreselectConsumed={() => setPreselectedRequisitionId(null)}
          />
        )}
        {page === "ai-interviews" && <AIInterviews />}
        {page === "cv" && <CVEvaluation />}
        {page === "candidates" && (
          <CandidateList
            statusFilter={statusFilter}
            locationFilter={locationFilter}
            requisitionFilter={requisitionFilter}
            onSelectCandidate={(c) => {
              setScreeningCandidate(c);
              setPage("screening");
            }}
          />
        )}
        {page === "screening" && <CandidateScreening preselected={screeningCandidate} statusFilter={statusFilter} locationFilter={locationFilter} requisitionFilter={requisitionFilter} />}
        {page === "dashboard" && <RecruitmentDashboard statusFilter={statusFilter} locationFilter={locationFilter} requisitionFilter={requisitionFilter} />}
        {page === "interviews" && <ScheduleInterviews statusFilter={statusFilter} locationFilter={locationFilter} requisitionFilter={requisitionFilter} />}
        {page === "communication" && <Communication statusFilter={statusFilter} locationFilter={locationFilter} requisitionFilter={requisitionFilter} />}
        {page === "status" && <CandidateStatus statusFilter={statusFilter} locationFilter={locationFilter} requisitionFilter={requisitionFilter} />}
        {page === "insights" && <Insights />}
        {page === "applicant" && <AgentChat agent="applicant" />}
        {page === "admin-settings" && <AdminSettings />}
      </main>
    </div>
  );
}

function App() {
  // Route: /portal → candidate portal. /interview/:token → public AI interview.
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const isPortal = path.startsWith("/portal");
  const isAIInterview = path.startsWith("/interview/");
  if (isAIInterview) {
    return <AIInterviewSession />;
  }
  if (isPortal) {
    return <CandidatePortal />;
  }
  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  );
}

export default App;
